import { useState } from 'react';

import { DEVICE_FAMILY_ALREADY_BOUND_ERROR_CODE } from '../../constants/devices.js';
import { useIntl, useSetDeviceFamily } from '../../hooks';
import { type ApiMutationError } from '../../lib/error.js';
import { cn } from '../../lib/utils';
import { DeviceFamily } from '../../validations/device-form.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui';

const familyLabelId = (family: DeviceFamily) =>
  `newDeviceForm.family${family.charAt(0).toUpperCase() + family.slice(1)}`;

type DeviceFamilyPickerProps = {
  deviceId: string;
  onAssigned: () => void;
  onAlreadyBound: () => void;
};

export function DeviceFamilyPicker({
  deviceId,
  onAssigned,
  onAlreadyBound,
}: DeviceFamilyPickerProps) {
  const intl = useIntl();
  const setDeviceFamily = useSetDeviceFamily();
  const [pendingFamily, setPendingFamily] = useState<DeviceFamily | null>(null);
  const [assignError, setAssignError] = useState<ApiMutationError | null>(null);

  const closeConfirmation = () => {
    setPendingFamily(null);
    setAssignError(null);
  };

  const confirmFamily = async () => {
    if (!pendingFamily) return;
    setAssignError(null);
    try {
      await setDeviceFamily.mutateAsync({ id: deviceId, family: pendingFamily });
      setPendingFamily(null);
      onAssigned();
    } catch (error) {
      const rejection = error as ApiMutationError;
      if (rejection.code === DEVICE_FAMILY_ALREADY_BOUND_ERROR_CODE) {
        // The device reported its own model first; the refetched binding is what the admin needs.
        closeConfirmation();
        onAlreadyBound();
        return;
      }
      setAssignError(rejection);
    }
  };

  return (
    <div className="mt-1 space-y-2" data-test-id="device-family-picker">
      <div
        className="flex gap-3"
        role="radiogroup"
        aria-label={intl.formatMessage({ id: 'deviceSettings.family.label' })}
      >
        {Object.values(DeviceFamily).map((family) => {
          const isSelected = pendingFamily === family;

          return (
            <label
              key={`device-family-${family}`}
              data-test-id={`device-family-option-${family}`}
              className={cn(
                'flex flex-1 cursor-pointer select-none items-center gap-3 rounded-lg border px-4 py-3 transition-colors',
                isSelected
                  ? 'border-black bg-background-dark'
                  : 'border-border hover:bg-background-light',
              )}
            >
              <input
                type="radio"
                name="deviceFamily"
                value={family}
                checked={isSelected}
                onChange={() => setPendingFamily(family)}
                className="sr-only"
              />
              <div
                className={cn(
                  'flex h-4 w-4 items-center justify-center rounded-full border',
                  isSelected ? 'border-black bg-black' : 'border-border',
                )}
              >
                {isSelected && <div className="h-2 w-2 rounded-full bg-background-light" />}
              </div>
              <span className="text-sm font-medium">
                {intl.formatMessage({ id: familyLabelId(family) })}
              </span>
            </label>
          );
        })}
      </div>
      <p className="text-xs text-foreground">
        {intl.formatMessage({ id: 'deviceSettings.family.hint' })}
      </p>

      <Dialog
        open={pendingFamily !== null}
        onOpenChange={(open) => {
          if (!open && !setDeviceFamily.isPending) closeConfirmation();
        }}
      >
        <DialogContent className="max-w-sm" data-test-id="device-family-confirm-dialog">
          <DialogHeader>
            <DialogTitle>
              {intl.formatMessage({ id: 'deviceSettings.family.confirmTitle' })}
            </DialogTitle>
            <DialogDescription>
              {pendingFamily &&
                intl.formatMessage(
                  { id: 'deviceSettings.family.confirmBody' },
                  { family: intl.formatMessage({ id: familyLabelId(pendingFamily) }) },
                )}
            </DialogDescription>
          </DialogHeader>
          {assignError && (
            <div
              role="alert"
              data-test-id="device-family-assign-error"
              className="rounded-md border border-destructive bg-destructive-light px-3 py-2 text-xs text-destructive-dark"
            >
              {intl.formatMessage({ id: 'deviceSettings.family.assignFailed' })}
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              data-test-id="device-family-cancel"
              onClick={closeConfirmation}
              disabled={setDeviceFamily.isPending}
            >
              {intl.formatMessage({ id: 'common.cancel' })}
            </Button>
            <Button
              type="button"
              data-test-id="device-family-confirm"
              onClick={confirmFamily}
              disabled={setDeviceFamily.isPending}
            >
              {intl.formatMessage({ id: 'deviceSettings.family.confirm' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
